import { getAccImportJobAPI } from '../common/services';
import useImportJob, { toCount } from './useImportJob';

const plural = (count) => `${count} document${count === 1 ? '' : 's'}`;

// A completed job can still have imported nothing (empty folders, every copy failed).
const describe = ({ metadata = {} }) => {
    const total = toCount(metadata.totalFiles);
    const imported = toCount(metadata.importedCount);
    const failed = toCount(metadata.failedCount);
    if (!total) return { success: false, message: 'No PDF, Word or Excel files were found in the selection.' };
    if (!failed) return { success: true, message: `Imported ${plural(imported)} from ACC.` };

    const failedNames = (metadata.failures || []).map((failure) => failure.displayName).filter(Boolean);
    const detail = failedNames.length ? ` (${failedNames.slice(0, 3).join(', ')}${failedNames.length > 3 ? ', …' : ''})` : '';
    return { success: imported > 0, message: `Imported ${imported} of ${plural(total)} from ACC; ${failed} failed${detail}.` };
};

// `totalFiles` is known once the selected folders are walked; until then only time tells.
const describeProgress = (job) => {
    const metadata = job?.metadata || {};
    const total = toCount(metadata.totalFiles);
    if (!total) return 'Importing your documents from ACC... this might take a while';
    const done = toCount(metadata.importedCount) + toCount(metadata.failedCount);
    return `Importing your documents from ACC... ${done} of ${plural(total)} done`;
};

/**
 * Tracks one ACC docs import job; a folder tree can take minutes to copy.
 *
 * @param {Function} options.onFinished - ({ success, message, job }) once the job ends
 * @returns {{ isImporting, trackImport, job, progressText }} `progressText` counts files as batches land
 */
const useAccDocsImport = ({ onFinished } = {}) => {
    const importJob = useImportJob({
        fetchJob: getAccImportJobAPI,
        describe,
        failureMessage: "The ACC documents couldn't be imported.",
        maxWaitMs: 30 * 60 * 1000,
        onFinished,
    });
    return { ...importJob, progressText: describeProgress(importJob.job) };
};

export default useAccDocsImport;
